import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import portalT from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft, Cog, Send, Loader, Lightbulb, MessageSquare,
  Zap, Clock, DollarSign, ChevronRight, Plus, BookOpen,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const AutomationAssistant = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.automation;
  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);

  const [view, setView] = useState('start'); // start | analysis | chat
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Analysis state
  const [workflowText, setWorkflowText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);

  
  const lastAssistant = (Array.isArray(messages) ? messages : []).filter(m => m.role === 'assistant').slice(-1)[0]?.content;
  usePublishToEmma('AI Automation Assistant', lastAssistant);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadTemplates(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${API}/compass/automation/templates`);
      setTemplates(res.data.templates);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const analyzeWorkflow = async (text) => {
    if (!((text) == null ? '' : String(text)).trim()) return;
    setAnalyzing(true);
    setView('analysis');
    try {
      const res = await axios.post(`${API}/compass/automation/analyze`, { description: text, language: lang }, { headers });
      setSessionId(res.data.session_id);
      setAnalysisResult(res.data.analysis);
      setMessages([
        { role: 'user', content: text },
        { role: 'assistant', content: res.data.analysis },
      ]);
    } catch (e) {
      setAnalysisResult('Something went wrong. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const sendChat = async (text) => {
    if (!((text) == null ? '' : String(text)).trim() || sending || !sessionId) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setChatInput('');
    setSending(true);
    try {
      const res = await axios.post(`${API}/compass/automation/chat`, { message: text, session_id: sessionId, language: lang }, { headers });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: lang === 'en' ? 'Sorry, something went wrong. Please try again.' : 'Sorry, er is iets misgegaan. Probeer het opnieuw.' }]);
    } finally {
      setSending(false);
    }
  };

  const generateRecipe = async (task) => {
    setMessages(prev => [...prev, { role: 'user', content: `Generate a detailed automation recipe for: ${task}` }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/compass/automation/recipe`, { task_description: task, session_id: sessionId, language: lang }, { headers });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.recipe }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Could not generate recipe.' }]);
    } finally {
      setSending(false);
    }
  };

  const startNew = () => {
    setView('start');
    setWorkflowText('');
    setAnalysisResult(null);
    setSessionId(null);
    setMessages([]);
  };

  if (loading) {
    return <div className="loading-screen"><div className="loader"></div><p>{t.common.loading}</p></div>;
  }

  // ─── Start View ───
  if (view === 'start') {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary"><ArrowLeft size={16} /> {t.common.aiTools}</button>
          <h1 className="heading-1"><Cog size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {tp.title}</h1>
        </div>
        <div className="tool-content">
          <div className="auto-start-section" data-testid="automation-start">
            {/* Workflow input */}
            <div className="auto-input-card" data-testid="workflow-input-card">
              <div className="auto-input-header">
                <Zap size={20} />
                <h3 className="heading-3">{tp.describeWorkflow}</h3>
              </div>
              <p className="body-small text-secondary" style={{ marginBottom: '1rem' }}>
                {lang === 'nl' ? 'Vertel ons over de taken die je herhaaldelijk doet. Hoe meer details je geeft, des te betere automatiseringssuggesties je krijgt.' : 'Tell us about the tasks you do repeatedly. The more detail you provide, the better automation suggestions you\'ll get.'}
              </p>
              <textarea
                ref={textareaRef}
                className="form-input auto-textarea"
                placeholder={tp.placeholder}
                value={workflowText}
                onChange={(e) => setWorkflowText(e.target.value)}
                rows={5}
                data-testid="workflow-textarea"
              />
              <button className="btn-primary" style={{ width: '100%', marginTop: '1rem' }}
                onClick={() => analyzeWorkflow(workflowText)} disabled={!((workflowText) == null ? '' : String(workflowText)).trim()}
                data-testid="analyze-btn">
                <Zap size={16} /> {tp.analyzeBtn}
              </button>
            </div>

            {/* Templates */}
            <div className="auto-templates" data-testid="templates-section">
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{lang === 'nl' ? 'Of kies een workflow template' : 'Or choose a workflow template'}</h3>
              <div className="auto-template-grid">
                {(Array.isArray(templates) ? templates : []).map((tmpl) => (
                  <button key={tmpl.id} className="auto-template-card" onClick={() => { setWorkflowText(tmpl.prompt); analyzeWorkflow(tmpl.prompt); }}
                    data-testid={`template-${tmpl.id}`}>
                    <div className="auto-template-top">
                      <Cog size={18} />
                      <ChevronRight size={16} className="compass-role-arrow" />
                    </div>
                    <h4 className="body-medium" style={{ fontWeight: 600 }}>{tmpl.title}</h4>
                    <p className="body-small text-secondary">{tmpl.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Analysis / Chat View ───
  return (
    <div className="compass-coach-page" data-testid="automation-workspace">
      <aside className="compass-sidebar">
        <div className="compass-sidebar-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>
            <ArrowLeft size={14} /> {t.common.aiTools}
          </button>
        </div>

        <div className="compass-role-badge" style={{ background: 'var(--accent-orange-200)' }}>
          <Cog size={16} />
          <span className="body-small" style={{ fontWeight: 500 }}>{lang === 'nl' ? 'Automatiseringsanalyse' : 'Automation Analysis'}</span>
        </div>

        <div className="auto-sidebar-actions">
          <button className="auto-action-btn" onClick={() => generateRecipe(lang === 'nl' ? 'de meest impactvolle automatisering uit onze analyse' : 'the most impactful automation from our analysis')} disabled={sending}
            data-testid="gen-recipe-btn">
            <BookOpen size={14} /> {lang === 'nl' ? 'Genereer Top Recept' : 'Generate Top Recipe'}
          </button>
          <button className="auto-action-btn" onClick={() => sendChat(lang === 'nl' ? 'Bereken de totale ROI als we alle voorgestelde automatiseringen implementeren. Toon tijd bespaard per week en geschatte jaarlijkse kostenbesparing.' : 'Calculate the total ROI if we implement all suggested automations. Show time saved per week and estimated annual cost savings.')} disabled={sending}
            data-testid="calc-roi-btn">
            <DollarSign size={14} /> {lang === 'nl' ? 'Bereken Volledige ROI' : 'Calculate Full ROI'}
          </button>
          <button className="auto-action-btn" onClick={() => sendChat(lang === 'nl' ? 'Maak een geprioriteerde implementatietijdlijn. Welke automatiseringen moeten we eerst, tweede, derde doen? Overweeg inspanning vs impact.' : 'Create a prioritized implementation timeline. Which automations should we do first, second, third? Consider effort vs impact.')} disabled={sending}
            data-testid="timeline-btn">
            <Clock size={14} /> {lang === 'nl' ? 'Implementatie Tijdlijn' : 'Implementation Timeline'}
          </button>
        </div>

        <button className="compass-new-chat" onClick={startNew} data-testid="new-analysis-btn">
          <Plus size={14} /> {t.common.newAnalysis}
        </button>
      </aside>

      <main className="compass-main">
        <div className="compass-chat-container">
          <div className="compass-chat-messages" data-testid="automation-messages">
            {analyzing && messages.length === 0 && (
              <div className="auto-analyzing-state">
                <Loader className="spin" size={32} />
                <h3 className="heading-3">{lang === 'nl' ? 'Je workflow analyseren...' : 'Analyzing your workflow...'}</h3>
                <p className="body-small text-secondary">{lang === 'nl' ? 'Repetitieve taken detecteren en automatiseringskansen vinden' : 'Detecting repetitive tasks and finding automation opportunities'}</p>
              </div>
            )}

            {(Array.isArray(messages) ? messages : []).map((msg, i) => (
              <div key={i} className={`compass-msg compass-msg-${msg.role}`} data-testid={`auto-msg-${i}`}>
                {msg.role === 'assistant' ? (
                  <div className="compass-msg-content markdown-content"><ReactMarkdown>{msg.content}</ReactMarkdown></div>
                ) : (
                  <div className="compass-msg-content">{msg.content}</div>
                )}
              </div>
            ))}

            {sending && (
              <div className="compass-msg compass-msg-assistant">
                <div className="compass-msg-content compass-typing"><Loader className="spin" size={16} /> {lang === 'nl' ? 'Bezig...' : 'Working on it...'}</div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Follow-up suggestions */}
          {analysisResult && messages.length <= 2 && !sending && (
            <div className="auto-followup-bar" data-testid="followup-suggestions">
              {(lang === 'nl' ? [
                "Met welke automatisering moet ik beginnen?",
                "Laat me een Zapier recept zien voor de makkelijkste taak",
                "Hoeveel tijd kan ik in totaal besparen?",
              ] : [
                "Which automation should I start with?",
                "Show me a Zapier recipe for the easiest task",
                "What's the total time I could save?",
              ]).map((s, i) => (
                <button key={i} className="compass-suggestion-btn" onClick={() => sendChat(s)} data-testid={`followup-${i}`}>
                  <Lightbulb size={14} /> {s}
                </button>
              ))}
            </div>
          )}

          <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); sendChat(chatInput); }} data-testid="automation-chat-form">
            <input type="text" value={chatInput} onChange={(e) => setChatInput(e.target.value)}
              placeholder={tp.chatPlaceholder}
              disabled={sending || analyzing} className="form-input" data-testid="automation-chat-input" />
            <button type="submit" disabled={sending || !((chatInput) == null ? '' : String(chatInput)).trim()} className="btn-primary compass-send-btn" data-testid="automation-send-btn">
              <Send size={16} />
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AutomationAssistant;
